import type {
  CreateEvaluationInput,
  CreateTaskInput,
  TemplatesUsed,
  UpdateTaskInput,
} from "../types/task";
import {
  QueryClient,
  QueryKey,
  QueryOptions,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "react-query";

import { TaskAPI, TaskFilters, TaskSortField } from "../api/taskApi";
import { message } from "antd";
import { useSearchQuery } from "utils/search/useSearchQuery";

// Updated interface to include filters and ordering
interface EmployeeTasksOptions {
  employeeId: number;
  positionId: number;
  page: number;
  filters?: TaskFilters;
  ordering?: TaskSortField;
}

type EmployeeTasksQueryKey = ["employeeTasks", EmployeeTasksOptions];

const invalidateUsedTemplates = (
  queryClient: QueryClient,
  entityType: "employee" | "position",
  entityId: number,
) => {
  queryClient.invalidateQueries([
    "templateTasks.get_used_templates",
    entityType,
    entityId,
  ]);
};

// Position Tasks Hooks
export const usePositionTasks = (
  positionId: number,
  page = 1,
  filters?: TaskFilters,
  ordering?: TaskSortField,
) => {
  return useQuery(
    ["positionTasks", Number(positionId), page, filters, ordering] as QueryKey,
    () => TaskAPI.getPositionTasks(positionId, page, filters, ordering),
    {
      staleTime: 10 * 60 * 1000, // 10 minutes
      enabled: !!positionId,
      keepPreviousData: true,
      retry: 0,
      refetchOnWindowFocus: false,
      onError: () => {
        message.error("Erro ao obter tarefas do cargo!");
      },
    },
  );
};

export const usePositionTaskMutations = (positionId: number) => {
  const queryClient = useQueryClient();

  const invalidateTasksForEmployeesInThisPosition = () => {
    queryClient.invalidateQueries({
      queryKey: ["employeeTasks"],
      predicate: (query) =>
        (query.queryKey as EmployeeTasksQueryKey)[1].positionId ===
        Number(positionId),
    });
  };

  const invalidateUsedTemplatesForPositions = (positionId: number) => {
    invalidateUsedTemplates(queryClient, "position", positionId);
  };

  const create = useMutation(
    (data: CreateTaskInput) =>
      TaskAPI.createPositionTask(Number(positionId), data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([
          "positionTasks",
          Number(positionId),
        ] as QueryKey);
        invalidateTasksForEmployeesInThisPosition();
        invalidateUsedTemplatesForPositions(positionId);
        message.success("Tarefa criada com sucesso!");
      },
      onError: () => {
        message.error("Erro ao criar tarefa!");
      },
    },
  );

  const update = useMutation(
    ({ id, data }: { id: string; data: UpdateTaskInput }) =>
      TaskAPI.updateTask(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([
          "positionTasks",
          Number(positionId),
        ] as QueryKey);
        message.success("Tarefa atualizada com sucesso!");
        invalidateTasksForEmployeesInThisPosition();
        invalidateUsedTemplatesForPositions(positionId);
      },
      onError: () => {
        message.error("Erro ao atualizar tarefa!");
      },
    },
  );

  const remove = useMutation((id: string) => TaskAPI.deleteTask(id), {
    onSuccess: () => {
      queryClient.invalidateQueries([
        "positionTasks",
        Number(positionId),
      ] as QueryKey);
      invalidateTasksForEmployeesInThisPosition();
      invalidateUsedTemplatesForPositions(positionId);
      message.success("Tarefa removida com sucesso!");
    },
    onError: () => {
      message.error("Erro ao remover tarefa!");
    },
  });

  return { create, update, remove };
};

// Employee Tasks Hooks - Updated to accept filters and ordering
export const useEmployeeTasks = (
  employeeId: number,
  positionId: number,
  page = 1,
  filters?: TaskFilters,
  ordering?: TaskSortField,
) => {
  return useQuery(
    [
      "employeeTasks",
      {
        employeeId: Number(employeeId),
        positionId: Number(positionId) || -1,
        page,
        filters,
        ordering,
      },
    ] as EmployeeTasksQueryKey,
    () => TaskAPI.getEmployeeTasks(employeeId, page, filters, ordering),
    {
      staleTime: 2 * 60 * 1000, // 2 minutes
      enabled: !!employeeId,
      keepPreviousData: true,
      retry: 0,
      refetchOnWindowFocus: false,
      onError: () => {
        message.error("Erro ao carregar tarefas do funcionário");
      },
    },
  );
};

export const useEmployeeTasksPermissions = (employeeId: number) => {
  return useQuery(
    ["employeeTasksPermissions", Number(employeeId)] as QueryKey,
    () => TaskAPI.getTaskPermissions(employeeId),
    {
      staleTime: 60 * 60 * 1000, // 1h
      enabled: !!employeeId,
      keepPreviousData: true,
      retry: 0,
      refetchOnWindowFocus: false,
      placeholderData: {
        "employee_tasks.can_view_tasks": false,
        "employee_tasks.can_create_tasks": false,
        "employee_tasks.can_evaluate_task": false,
        "employee_tasks.can_view_evaluations": false,
        "employee_tasks.can_edit_task": false,
        "employee_tasks.can_edit_template_task": false,
        "employee_tasks.can_delete_task": false,
      },
      onError: () => {
        message.error("Erro ao carregar permissões de tarefas do funcionário");
      },
    },
  );
};

const invalidateTasksForEmployee = (
  queryClient: QueryClient,
  employeeId: number | undefined | string,
) => {
  queryClient.invalidateQueries({
    queryKey: ["employeeTasks"],
    predicate: (query) =>
      (query.queryKey as EmployeeTasksQueryKey)[1].employeeId ===
      Number(employeeId),
  });
};

export const useEmployeeTaskMutations = (employeeId: number) => {
  const queryClient = useQueryClient();

  const invalidateUsedTemplatesForEmployee = (employeeId: number) => {
    invalidateUsedTemplates(queryClient, "employee", employeeId);
  };

  const create = useMutation(
    (data: CreateTaskInput) => TaskAPI.createEmployeeTask(employeeId, data),
    {
      onSuccess: () => {
        invalidateTasksForEmployee(queryClient, employeeId);
        invalidateUsedTemplatesForEmployee(employeeId);
      },
      onError: () => {
        message.error("Erro ao criar tarefa!");
      },
    },
  );

  const update = useMutation(
    ({ id, data }: { id: string; data: UpdateTaskInput }) =>
      TaskAPI.updateTask(id, data),
    {
      onSuccess: () => {
        invalidateTasksForEmployee(queryClient, employeeId);
        invalidateUsedTemplatesForEmployee(employeeId);
      },
      onError: () => {
        message.error("Erro ao atualizar tarefa!");
      },
    },
  );

  const remove = useMutation((id: string) => TaskAPI.deleteTask(id), {
    onSuccess: () => {
      invalidateTasksForEmployee(queryClient, employeeId);
      invalidateUsedTemplatesForEmployee(employeeId);
    },
    onError: () => {
      message.error("Erro ao deletar tarefa!");
    },
  });

  const ignore = useMutation(
    ({ id, reason }: { id: string; reason?: string }) =>
      TaskAPI.ignoreTask(id, employeeId, reason),
    {
      onSuccess: () => {
        invalidateTasksForEmployee(queryClient, employeeId);
      },
      onError: () => {
        message.error("Erro ao ignorar tarefa!");
      },
    },
  );

  return { create, update, remove, ignore };
};

// Template Tasks Hooks
export const useTemplateTask = (templateId: string) => {
  return useQuery(
    ["templateTasks", templateId] as QueryKey,
    ({ signal }) => TaskAPI.getTemplateTask(templateId, signal),
    {
      staleTime: 1 * 60 * 1000, // 1 minutes
      enabled: !!templateId,
      retry: 0,
      onError: () => {
        message.error("Erro ao obter template de id=" + templateId);
      },
    },
  );
};

export type TemplateUsedParams = {
  model: string;
  id: number;
};

export const useTemplatesUsed = (filters: TemplateUsedParams[]) => {
  const results = useQueries(
    filters.map(
      (filter) =>
        ({
          queryKey: [
            "templateTasks.get_used_templates",
            filter.model,
            filter.id,
          ] as QueryKey,
          queryFn: ({ signal }) =>
            TaskAPI.getTemplateUsedFor(filter.id, filter.model, signal),
          staleTime: 24 * 60 * 60 * 1000, // 24h
          cacheTime: 24 * 60 * 60 * 1000, // 24h
          enabled: !!filter.id && !!filter.model,
          retry: 0,
          onError: () => {
            message.error("Erro ao obter templates usados");
          },
        }) as QueryOptions,
    ),
  );
  return results.flatMap((result) => result.data as TemplatesUsed);
};

export const useTemplateTasks = (
  search: string,
  filters: Object = {},
  options?: {
    minCharacters?: number;
    debounceDelay?: number;
    throttleDelay?: number;
    loadFirstPage?: boolean;
  },
) => {
  const result = useSearchQuery({
    queryKeyPrefix: ["templateTasks"],
    searchTerm: search,
    filters: filters,
    queryFn: async (search: string, filters: any, signal?: AbortSignal) => {
      return TaskAPI.getTemplateTasks(search, signal);
    },
    options: {
      searchConfig: {
        minCharacters: 0,
        ...options,
        loadFirstPage: options?.loadFirstPage || true,
      },
      queryOptions: {
        select: (data) =>
          data?.results ||
          ([] as unknown as ReturnType<typeof TaskAPI.getTemplateTasks>),
      },
    },
  });

  return result;
};

export const useCreateTemplateTask = ({
  options,
}: {
  options?: { onSuccess?: (data: any) => void };
}) => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ name, description }: { name: string; description?: string }) =>
      TaskAPI.createTemplateTask(name, description),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(["templateTasks"] as QueryKey);
        options?.onSuccess?.(data);
      },
    },
  );
};

// Evaluation Hooks
export const useEvaluationOptions = () => {
  return useQuery(
    ["evaluationOptions"] as QueryKey,
    TaskAPI.getEvaluationOptions,
    {
      staleTime: 10 * 60 * 1000, // Cache for 10 minutes,
      select: (data) => data.results,
      retry: 0,
      refetchOnWindowFocus: false,
    },
  );
};

export const useCreateEvaluation = (employeeId: number) => {
  const queryClient = useQueryClient();

  return useMutation(
    (data: CreateEvaluationInput) => TaskAPI.createEvaluation(employeeId, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([
          "employeeEvaluations",
          Number(employeeId),
        ] as QueryKey);
        invalidateTasksForEmployee(queryClient, employeeId);
      },
      onError: () => {
        message.error("Erro ao salvar avaliação!");
      },
    },
  );
};

export const useEmployeeEvaluations = (employeeId: number, enabled = true) => {
  return useQuery(
    ["employeeEvaluations", Number(employeeId)] as QueryKey,
    () => TaskAPI.getEmployeeEvaluations(employeeId),
    {
      staleTime: 1 * 60 * 1000, // 1 minutes
      enabled: !!employeeId && enabled,
      retry: 0,
      onError: () => {
        message.error("Erro ao obter avaliações!");
      },
    },
  );
};
